import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../utils/Layout'
import { PageHeader } from '../simulador/ui'
import UsersTable from '../components/UsersTable'
import userManagementService from '../services/userManagement'
import enrollmentsService from '../services/enrollments'
import apiService from '../services/api'

const AdminUsers = () => {
    const navigate = useNavigate()

    // Estados principales
    const [allUsers, setAllUsers] = useState([]) // TODOS los usuarios
    const [filteredUsers, setFilteredUsers] = useState([]) // Usuarios filtrados
    const [currentPage, setCurrentPage] = useState(1)
    const [usersPerPage] = useState(20)
    const [userStats, setUserStats] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    // Estados para la nueva funcionalidad de Cursos
    const [activeTab, setActiveTab] = useState('users') // 'users' | 'courses'
    const [courses, setCourses] = useState([])
    const [courseEnrollments, setCourseEnrollments] = useState([])
    const [loadingCourses, setLoadingCourses] = useState(false)
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [showCourseStudentsModal, setShowCourseStudentsModal] = useState(false)

    // Estados para gestión de accesos en el modal
    const [selectedStudents, setSelectedStudents] = useState([]) // IDs de inscripciones seleccionadas
    const [actionLoading, setActionLoading] = useState(false)
    const [studentFilter, setStudentFilter] = useState('todos') // 'todos' | 'activos' | 'inactivos'
    const [studentSearch, setStudentSearch] = useState('') // Búsqueda por nombre o usuario
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmAction, setConfirmAction] = useState(null) // 'suspend' | 'reactivate'

    // Estados para modales/formularios
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [showEditForm, setShowEditForm] = useState(false)
    const [showViewModal, setShowViewModal] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showPasswordReset, setShowPasswordReset] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [formLoading, setFormLoading] = useState(false)

    // Estados del formulario
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        nombre_completo: '',
        nombre_usuario: '',
        telefono: '',
        tipo_usuario: 'estudiante',
        activo: true
    })
    const [formErrors, setFormErrors] = useState({})
    const [newPassword, setNewPassword] = useState('')

    // Estados de filtros (sin paginación)
    const [filters, setFilters] = useState({
        search: '',
        tipo: '',
        activo: ''
    })

    // Configuraciones
    const tiposUsuario = [
        { value: '', label: 'Todos los tipos' },
        { value: 'estudiante', label: 'Estudiantes' },
        { value: 'instructor', label: 'Instructores' },
        { value: 'admin', label: 'Administradores' }
    ]

    const estadosUsuario = [
        { value: '', label: 'Todos los estados' },
        { value: 'true', label: 'Activos' },
        { value: 'false', label: 'Inactivos' }
    ]

    // Calcular paginación en el cliente
    const indexOfLastUser = currentPage * usersPerPage
    const indexOfFirstUser = indexOfLastUser - usersPerPage
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage)

    const pagination = {
        page: currentPage,
        totalPages: totalPages,
        total: filteredUsers.length,
        limit: usersPerPage
    }

    // Cargar usuarios una sola vez
    const loadUsers = async () => {
        try {
            setLoading(true)
            setError('')

            const result = await userManagementService.getAllUsers()

            if (result.success) {
                setAllUsers(result.data.usuarios || [])
                setFilteredUsers(result.data.usuarios || [])
            } else {
                setError(result.error || 'Error cargando usuarios')
            }
        } catch (error) {
            setError('Error de conexión')
        } finally {
            setLoading(false)
        }
    }

    const loadUserStats = async () => {
        try {
            const result = await userManagementService.getUserStats()
            if (result.success) {
                setUserStats(result.data)
            }
        } catch (error) {
            // Silent fail
        }
    }

    // Función para cargar cursos con información de estudiantes inscritos
    const loadCoursesWithEnrollments = async () => {
        try {
            setLoadingCourses(true)
            setError('')

            // Cargar cursos
            const coursesResult = await apiService.get('/courses')

            // Cargar todas las inscripciones
            const enrollmentsResult = await enrollmentsService.getAllEnrollments({})

            if (coursesResult.success && enrollmentsResult.success) {
                const allCourses = coursesResult.data.cursos || []
                const allEnrollments = enrollmentsResult.data.inscripciones || []

                // Agrupar inscripciones por curso
                const enrollmentsByCourse = allEnrollments.reduce((acc, enrollment) => {
                    if (!acc[enrollment.curso_id]) {
                        acc[enrollment.curso_id] = []
                    }
                    acc[enrollment.curso_id].push(enrollment)
                    return acc
                }, {})

                // Enriquecer cursos con información de inscripciones
                const coursesWithData = allCourses.map(course => ({
                    ...course,
                    inscripciones: enrollmentsByCourse[course.id] || [],
                    total_inscritos: (enrollmentsByCourse[course.id] || []).length,
                    pendientes: (enrollmentsByCourse[course.id] || []).filter(e => e.estado_pago === 'pendiente').length,
                    habilitados: (enrollmentsByCourse[course.id] || []).filter(e => e.estado_pago === 'habilitado').length,
                    rechazados: (enrollmentsByCourse[course.id] || []).filter(e => e.estado_pago === 'rechazado').length
                }))

                setCourses(coursesWithData)
                setCourseEnrollments(allEnrollments)
            } else {
                setError('Error cargando cursos o inscripciones')
            }
        } catch (error) {
            console.error('Error cargando cursos:', error)
            setError('Error de conexión al cargar cursos')
        } finally {
            setLoadingCourses(false)
        }
    }

    // Filtrar usuarios en el cliente
    useEffect(() => {
        let filtered = [...allUsers]

        // Filtro por búsqueda
        if (filters.search) {
            const searchLower = filters.search.toLowerCase().trim()
            filtered = filtered.filter(user =>
                user.nombre_completo?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.nombre_usuario?.toLowerCase().includes(searchLower)
            )
        }

        // Filtro por tipo
        if (filters.tipo) {
            filtered = filtered.filter(user => user.tipo_usuario === filters.tipo)
        }

        // Filtro por estado
        if (filters.activo !== '') {
            const isActive = filters.activo === 'true'
            filtered = filtered.filter(user => user.activo === isActive)
        }

        setFilteredUsers(filtered)
        setCurrentPage(1) // Reset a página 1 cuando cambien filtros
    }, [allUsers, filters])

    // Efectos
    useEffect(() => {
        loadUsers()
        loadUserStats()
    }, [])

    // Cargar cursos cuando se cambie a la pestaña de cursos
    useEffect(() => {
        if (activeTab === 'courses' && courses.length === 0) {
            loadCoursesWithEnrollments()
        }
    }, [activeTab])

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000)
            return () => clearTimeout(timer)
        }
    }, [success])

    // Handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage)
        }
    }

    const handleFilterChange = (e) => {
        const { name, value } = e.target
        setFilters(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const clearFilters = () => {
        setFilters({
            search: '',
            tipo: '',
            activo: ''
        })
    }

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            nombre_completo: '',
            nombre_usuario: '',
            telefono: '',
            tipo_usuario: 'estudiante',
            activo: true
        })
        setFormErrors({})
        setSelectedUser(null)
        setNewPassword('')
    }

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        if (name === 'email' && !showEditForm) {
            const username = value.split('@')[0]
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '')

            setFormData(prev => ({
                ...prev,
                nombre_usuario: username
            }))
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }))
        }
    }

    const validateForm = () => {
        const validation = userManagementService.validateUserData(formData)
        setFormErrors(validation.errors)
        return validation.isValid
    }

    const handleCreateUser = () => {
        resetForm()
        setShowCreateForm(true)
        setShowEditForm(false)
    }

    const handleUserEdit = (user) => {
        setFormData({
            email: user.email || '',
            password: '',
            nombre_completo: user.nombre_completo || '',
            nombre_usuario: user.nombre_usuario || '',
            telefono: user.telefono || '',
            tipo_usuario: user.tipo_usuario || 'estudiante',
            activo: Boolean(user.activo)
        })
        setSelectedUser(user)
        setShowEditForm(true)
        setShowCreateForm(false)
        setFormErrors({})
    }

    const handleUserView = async (user) => {
        try {
            setFormLoading(true)
            const result = await userManagementService.getUserById(user.id)

            if (result.success) {
                setSelectedUser(result.data.usuario)
                setShowViewModal(true)
            } else {
                setError('Error cargando detalles del usuario')
            }
        } catch (error) {
            setError('Error de conexión')
        } finally {
            setFormLoading(false)
        }
    }

    const handleUserDelete = (user) => {
        setSelectedUser(user)
        setShowDeleteConfirm(true)
    }

    const handleUserPasswordReset = (user) => {
        setSelectedUser(user)
        setNewPassword('')
        setShowPasswordReset(true)
    }

    const handleUserToggleStatus = async (user) => {
        try {
            const result = await userManagementService.toggleUserStatus(user.id, !user.activo)

            if (result.success) {
                // Actualizar el usuario en la lista local
                setAllUsers(prev => prev.map(u =>
                    u.id === user.id
                        ? { ...u, activo: !u.activo }
                        : u
                ))
                setSuccess(`Usuario ${!user.activo ? 'activado' : 'desactivado'} exitosamente`)
            } else {
                setError(result.error || 'Error cambiando estado')
            }
        } catch (error) {
            setError('Error de conexión')
        }
    }

    const handleUserChangeRole = async (user, newRole) => {
        if (user.tipo_usuario === newRole) return

        try {
            const result = await userManagementService.updateUserRole(user.id, newRole)

            if (result.success) {
                // Actualizar el usuario en la lista local
                setAllUsers(prev => prev.map(u =>
                    u.id === user.id
                        ? { ...u, tipo_usuario: newRole }
                        : u
                ))
                setSuccess(`Rol actualizado a ${userManagementService.getRoleLabel(newRole)}`)
            } else {
                setError(result.error || 'Error cambiando rol')
            }
        } catch (error) {
            setError('Error de conexión')
        }
    }

    const handleUserViewProgress = (user) => {
        navigate(`/admin/usuarios/${user.id}`)
    }

    const handleSubmitForm = async (e) => {
        e.preventDefault()

        if (!validateForm()) return

        setFormLoading(true)

        try {
            let result
            if (showEditForm && selectedUser) {
                const updateData = { ...formData }
                if (!updateData.password) {
                    delete updateData.password
                }
                result = await userManagementService.updateUser(selectedUser.id, updateData)
            } else {
                result = await userManagementService.createUser(formData)
            }

            if (result.success) {
                setShowCreateForm(false)
                setShowEditForm(false)
                resetForm()

                // Recargar usuarios después de crear/editar
                await loadUsers()
                await loadUserStats()

                setSuccess(result.message || (showEditForm ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente'))
            } else {
                setError(result.error || 'Error procesando el usuario')
            }
        } catch (error) {
            setError('Error de conexión al procesar el usuario')
        } finally {
            setFormLoading(false)
        }
    }

    const confirmDeleteUser = async () => {
        if (!selectedUser) return

        try {
            setFormLoading(true)
            const result = await userManagementService.deleteUser(selectedUser.id)

            if (result.success) {
                setShowDeleteConfirm(false)
                setSelectedUser(null)

                // Remover usuario de la lista local
                setAllUsers(prev => prev.filter(u => u.id !== selectedUser.id))
                await loadUserStats()

                setSuccess(result.message || 'Usuario eliminado exitosamente')
            } else {
                setError(result.error || 'Error eliminando el usuario')
            }
        } catch (error) {
            setError('Error de conexión al eliminar el usuario')
        } finally {
            setFormLoading(false)
        }
    }

    const confirmPasswordReset = async () => {
        if (!selectedUser || !newPassword || newPassword.length < 6) {
            setError('La nueva contraseña debe tener al menos 6 caracteres')
            return
        }

        try {
            setFormLoading(true)
            const result = await userManagementService.resetUserPassword(selectedUser.id, newPassword)

            if (result.success) {
                setShowPasswordReset(false)
                setSelectedUser(null)
                setNewPassword('')
                setSuccess(`Contraseña reseteada para ${result.data.usuario.nombre_completo}`)
            } else {
                setError(result.error || 'Error reseteando contraseña')
            }
        } catch (error) {
            setError('Error de conexión')
        } finally {
            setFormLoading(false)
        }
    }

    const handleCancelForm = () => {
        setShowCreateForm(false)
        setShowEditForm(false)
        resetForm()
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Nunca'
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getUserStatusColor = (user) => {
        if (!user.activo) return 'bg-red-100 text-red-800'
        return 'bg-green-100 text-green-800'
    }

    const getUserStatusText = (user) => {
        return user.activo ? 'Activo' : 'Inactivo'
    }

    // Handlers para la funcionalidad de Cursos
    const handleViewCourseStudents = (course) => {
        setSelectedCourse(course)
        setShowCourseStudentsModal(true)
        setSelectedStudents([]) // Limpiar selección
        setStudentFilter('todos') // Resetear filtro
        setStudentSearch('') // Limpiar búsqueda
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-EC', {
            style: 'currency',
            currency: 'USD'
        }).format(amount)
    }

    // Obtener estudiantes filtrados (solo habilitados)
    const getFilteredStudents = () => {
        if (!selectedCourse || !selectedCourse.inscripciones) return []

        // Solo mostrar estudiantes habilitados
        let students = selectedCourse.inscripciones.filter(
            enrollment => enrollment.estado_pago === 'habilitado'
        )

        // Aplicar filtro de activos/inactivos
        if (studentFilter === 'activos') {
            students = students.filter(s => s.acceso_activo === true)
        } else if (studentFilter === 'inactivos') {
            students = students.filter(s => s.acceso_activo === false)
        }

        // Aplicar búsqueda por nombre o usuario
        if (studentSearch.trim()) {
            const searchTerm = studentSearch.toLowerCase().trim()
            students = students.filter(s =>
                s.nombre_completo?.toLowerCase().includes(searchTerm) ||
                s.nombre_usuario?.toLowerCase().includes(searchTerm) ||
                s.email?.toLowerCase().includes(searchTerm)
            )
        }

        return students
    }

    // Manejar selección individual
    const handleSelectStudent = (inscripcionId) => {
        setSelectedStudents(prev => {
            if (prev.includes(inscripcionId)) {
                return prev.filter(id => id !== inscripcionId)
            } else {
                return [...prev, inscripcionId]
            }
        })
    }

    // Seleccionar todos los visibles
    const handleSelectAll = () => {
        const filteredStudents = getFilteredStudents()
        if (selectedStudents.length === filteredStudents.length) {
            setSelectedStudents([])
        } else {
            setSelectedStudents(filteredStudents.map(s => s.id))
        }
    }

    // Mostrar modal de confirmación para desactivar
    const handleSuspendAccess = () => {
        if (selectedStudents.length === 0) {
            setError('Debes seleccionar al menos un estudiante')
            return
        }
        setConfirmAction('suspend')
        setShowConfirmModal(true)
    }

    // Mostrar modal de confirmación para reactivar
    const handleReactivateAccess = () => {
        if (selectedStudents.length === 0) {
            setError('Debes seleccionar al menos un estudiante')
            return
        }
        setConfirmAction('reactivate')
        setShowConfirmModal(true)
    }

    // Ejecutar la acción confirmada
    const executeConfirmedAction = async () => {
        setActionLoading(true)
        setError('')
        setShowConfirmModal(false)

        const endpoint = confirmAction === 'suspend' ? '/enrollments/suspend' : '/enrollments/reactivate'
        const successMessage = confirmAction === 'suspend'
            ? 'Accesos desactivados exitosamente'
            : 'Accesos reactivados exitosamente'

        try {
            const response = await apiService.post(endpoint, {
                inscripcionIds: selectedStudents
            })

            if (response.success) {
                setSuccess(successMessage)

                // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE
                const newAccesoActivo = confirmAction === 'reactivate'

                setSelectedCourse(prev => ({
                    ...prev,
                    inscripciones: prev.inscripciones.map(enrollment =>
                        selectedStudents.includes(enrollment.id)
                            ? { ...enrollment, acceso_activo: newAccesoActivo }
                            : enrollment
                    )
                }))

                // Actualizar también en la lista de cursos
                setCourses(prevCourses => prevCourses.map(course =>
                    course.id === selectedCourse.id
                        ? {
                            ...course,
                            inscripciones: course.inscripciones.map(enrollment =>
                                selectedStudents.includes(enrollment.id)
                                    ? { ...enrollment, acceso_activo: newAccesoActivo }
                                    : enrollment
                            )
                        }
                        : course
                ))

                setSelectedStudents([])
            } else {
                setError(response.error || `Error al ${confirmAction === 'suspend' ? 'desactivar' : 'reactivar'} accesos`)
            }
        } catch (error) {
            console.error('Error:', error)
            setError(`Error de conexión al ${confirmAction === 'suspend' ? 'desactivar' : 'reactivar'} accesos`)
        } finally {
            setActionLoading(false)
            setConfirmAction(null)
        }
    }

    return (
        <Layout showSidebar={true}>
            <div className="p-6 md:p-8">
                {/* Header */}
                <PageHeader
                    eyebrow="Administración"
                    title="Gestión de Usuarios"
                    subtitle={(
                        <>
                            Administra todos los usuarios de la plataforma
                            {filteredUsers.length !== allUsers.length && (
                                <span className="text-medico-blue font-medium">
                                   {' '}({filteredUsers.length} de {allUsers.length} usuarios)
                               </span>
                            )}
                        </>
                    )}
                    actions={!showCreateForm && !showEditForm && (
                        <button
                            onClick={handleCreateUser}
                            className="bg-medico-blue text-white px-6 py-3 rounded-full hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Crear Usuario</span>
                        </button>
                    )}
                />

                {/* Mensajes */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-red-600">{error}</p>
                        </div>
                        <button
                            onClick={() => setError('')}
                            className="mt-2 text-red-700 underline hover:no-underline text-sm"
                        >
                            Cerrar
                        </button>
                    </div>
                )}

                {success && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-green-600">{success}</p>
                        </div>
                    </div>
                )}

                {/* Tabs para cambiar entre Usuarios y Cursos */}
                {!showCreateForm && !showEditForm && (
                    <div className="mb-6">
                        <div className="border-b border-gray-200">
                            <nav className="-mb-px flex space-x-8">
                                <button
                                    onClick={() => setActiveTab('users')}
                                    className={`${
                                        activeTab === 'users'
                                            ? 'border-medico-blue text-medico-blue'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                    </svg>
                                    <span>Usuarios</span>
                                    <span className="bg-medico-blue text-white text-xs px-2 py-1 rounded-full">
                                        {allUsers.length}
                                    </span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('courses')}
                                    className={`${
                                        activeTab === 'courses'
                                            ? 'border-medico-blue text-medico-blue'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    <span>Cursos</span>
                                    <span className="bg-medico-blue text-white text-xs px-2 py-1 rounded-full">
                                        {courses.length}
                                    </span>
                                </button>
                            </nav>
                        </div>
                    </div>
                )}

                {/* Estadísticas */}
                {!showCreateForm && !showEditForm && activeTab === 'users' && userStats.usuarios && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Total Usuarios</h3>
                                    <p className="text-3xl font-bold text-medico-blue">{allUsers.length}</p>
                                </div>
                                <div className="bg-medico-blue bg-opacity-10 rounded-full p-3">
                                    <svg className="w-8 h-8 text-medico-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Estudiantes</h3>
                                    <p className="text-3xl font-bold text-green-600">
                                        {allUsers.filter(u => u.tipo_usuario === 'estudiante').length}
                                    </p>
                                </div>
                                <div className="bg-green-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Instructores</h3>
                                    <p className="text-3xl font-bold text-blue-600">
                                        {allUsers.filter(u => u.tipo_usuario === 'instructor').length}
                                    </p>
                                </div>
                                <div className="bg-blue-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Activos</h3>
                                    <p className="text-3xl font-bold text-purple-600">
                                        {allUsers.filter(u => u.activo).length}
                                    </p>
                                </div>
                                <div className="bg-purple-100 rounded-full p-3">
                                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filtros */}
                {!showCreateForm && !showEditForm && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                                <input
                                    type="text"
                                    name="search"
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    placeholder="Nombre, email, usuario..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-medico-blue focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                                <select
                                    name="tipo"
                                    value={filters.tipo}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-medico-blue focus:border-transparent"
                                >
                                    {tiposUsuario.map(tipo => (
                                        <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                <select
                                    name="activo"
                                    value={filters.activo}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-medico-blue focus:border-transparent"
                                >
                                    {estadosUsuario.map(estado => (
                                        <option key={estado.value} value={estado.value}>{estado.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    onClick={clearFilters}
                                    className="w-full px-3 py-2 text-medico-blue border border-medico-blue rounded-full hover:bg-medico-blue hover:text-white transition-colors"
                                >
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Formulario de crear/editar */}
                {(showCreateForm || showEditForm) && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-medico-blue">
                                {showEditForm ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                            </h2>
                            <button
                                onClick={handleCancelForm}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmitForm} className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleFormChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-medico-blue focus:border-transparent ${
                                            formErrors.email ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="usuario@ejemplo.com"
                                    />
                                    {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre de Usuario *
                                    </label>
                                    <input
                                        type="text"
                                        name="nombre_usuario"
                                        value={formData.nombre_usuario}
                                        onChange={handleFormChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-medico-blue focus:border-transparent ${
                                            formErrors.nombre_usuario ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="nombre_usuario"
                                    />
                                    {formErrors.nombre_usuario && <p className="mt-1 text-sm text-red-600">{formErrors.nombre_usuario}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre Completo *
                                    </label>
                                    <input
                                        type="text"
                                        name="nombre_completo"
                                        value={formData.nombre_completo}
                                        onChange={handleFormChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-medico-blue focus:border-transparent ${
                                            formErrors.nombre_completo ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="Juan Pérez"
                                    />
                                    {formErrors.nombre_completo && <p className="mt-1 text-sm text-red-600">{formErrors.nombre_completo}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Teléfono
                                    </label>
                                    <input
                                        type="tel"
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleFormChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-medico-blue focus:border-transparent"
                                        placeholder="+593 99 123 4567"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {showEditForm ? 'Nueva Contraseña (opcional)' : 'Contraseña *'}
                                    </label>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleFormChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-medico-blue focus:border-transparent ${
                                            formErrors.password ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder={showEditForm ? 'Dejar vacío para mantener actual' : 'Mínimo 6 caracteres'}
                                    />
                                    {formErrors.password && <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo de Usuario *
                                    </label>
                                    <select
                                        name="tipo_usuario"
                                        value={formData.tipo_usuario}
                                        onChange={handleFormChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-medico-blue focus:border-transparent ${
                                            formErrors.tipo_usuario ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    >
                                        <option value="estudiante">Estudiante</option>
                                        <option value="instructor">Instructor</option>
                                        <option value="admin">Administrador</option>
                                    </select>
                                    {formErrors.tipo_usuario && <p className="mt-1 text-sm text-red-600">{formErrors.tipo_usuario}</p>}
                                </div>

                                <div className="lg:col-span-2">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            name="activo"
                                            checked={formData.activo}
                                            onChange={handleFormChange}
                                            className="mr-2 rounded"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Usuario Activo</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleCancelForm}
                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={formLoading}
                                    className="px-6 py-2 bg-medico-blue text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                                >
                                    {formLoading && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                    <span>{formLoading ? 'Procesando...' : (showEditForm ? 'Actualizar Usuario' : 'Crear Usuario')}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Tabla de usuarios */}
                {!showCreateForm && !showEditForm && activeTab === 'users' && (
                    <UsersTable
                        users={currentUsers}
                        pagination={pagination}
                        loading={loading}
                        onPageChange={handlePageChange}
                        onUserEdit={handleUserEdit}
                        onUserView={handleUserView}
                        onUserDelete={handleUserDelete}
                        onUserToggleStatus={handleUserToggleStatus}
                        onUserChangeRole={handleUserChangeRole}
                        onUserPasswordReset={handleUserPasswordReset}
                        onUserViewProgress={handleUserViewProgress}
                    />
                )}

                {/* Tabla de Cursos con Estudiantes */}
                {!showCreateForm && !showEditForm && activeTab === 'courses' && (
                    <div>
                        {loadingCourses ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medico-blue mx-auto"></div>
                                    <p className="mt-4 text-medico-gray">Cargando cursos...</p>
                                </div>
                            </div>
                        ) : courses.length > 0 ? (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Curso
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Precio
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total Inscritos
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Habilitados
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Pendientes
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Rechazados
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {courses.map((course) => (
                                                <tr key={course.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="flex-shrink-0 h-16 w-16">
                                                                {course.miniatura_url ? (
                                                                    <img
                                                                        className="h-16 w-16 rounded object-cover"
                                                                        src={course.miniatura_url}
                                                                        alt={course.titulo}
                                                                    />
                                                                ) : (
                                                                    <div className="h-16 w-16 rounded bg-medico-blue flex items-center justify-center">
                                                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {course.titulo}
                                                                </div>
                                                                <div className="text-sm text-gray-500">
                                                                    {course.es_gratuito && (
                                                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                                            Gratuito
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-bold text-medico-blue">
                                                            {course.es_gratuito ? 'Gratuito' : formatCurrency(parseFloat(course.precio))}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="text-2xl font-bold text-medico-blue">
                                                            {course.total_inscritos}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="text-2xl font-bold text-green-600">
                                                            {course.habilitados}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="text-2xl font-bold text-yellow-600">
                                                            {course.pendientes}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="text-2xl font-bold text-red-600">
                                                            {course.rechazados}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <button
                                                            onClick={() => handleViewCourseStudents(course)}
                                                            className="inline-flex items-center px-3 py-2 border border-medico-blue text-sm font-medium rounded-md text-medico-blue bg-white hover:bg-medico-blue hover:text-white transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                                            </svg>
                                                            Ver Estudiantes
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay cursos disponibles</h3>
                                <p className="text-gray-500">No se encontraron cursos en el sistema</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Modal de visualización */}
                {showViewModal && selectedUser && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-4xl mx-4 max-h-90vh overflow-y-auto">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold text-medico-blue">Detalles del Usuario</h2>
                                <button
                                    onClick={() => setShowViewModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-2">
                                    <div className="flex items-center mb-6">
                                        {selectedUser.avatar_url ? (
                                            <img className="h-20 w-20 rounded-full" src={selectedUser.avatar_url} alt="" />
                                        ) : (
                                            <div className="h-20 w-20 rounded-full bg-medico-blue flex items-center justify-center">
                                              <span className="text-white font-bold text-2xl">
                                                  {selectedUser.nombre_completo?.charAt(0)?.toUpperCase() || 'U'}
                                              </span>
                                            </div>
                                        )}
                                        <div className="ml-6">
                                            <h3 className="text-xl font-semibold text-gray-900">{selectedUser.nombre_completo}</h3>
                                            <p className="text-gray-600">@{selectedUser.nombre_usuario}</p>
                                            <div className="flex items-center space-x-3 mt-2">
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${userManagementService.getRoleColor(selectedUser.tipo_usuario)}`}>
                                                  {userManagementService.getRoleLabel(selectedUser.tipo_usuario)}
                                              </span>
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUserStatusColor(selectedUser)}`}>
                                                  {getUserStatusText(selectedUser)}
                                              </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-700">Email:</span>
                                            <p className="text-gray-600">{selectedUser.email}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Teléfono:</span>
                                            <p className="text-gray-600">{selectedUser.telefono || 'No registrado'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Fecha de registro:</span>
                                            <p className="text-gray-600">{formatDate(selectedUser.fecha_registro)}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Último acceso:</span>
                                            <p className="text-gray-600">{formatDate(selectedUser.ultima_conexion)}</p>
                                        </div>
                                    </div>

                                    {selectedUser.inscripciones && selectedUser.inscripciones.length > 0 && (
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Inscripciones</h4>
                                            <div className="space-y-3">
                                                {selectedUser.inscripciones.map((inscripcion) => (
                                                    <div key={inscripcion.id} className="border border-gray-200 rounded-lg p-4">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h5 className="font-medium text-gray-900">{inscripcion.titulo}</h5>
                                                                <p className="text-sm text-gray-600">
                                                                    Inscrito: {formatDate(inscripcion.fecha_inscripcion)}
                                                                </p>
                                                                {inscripcion.instructor_nombre && (
                                                                    <p className="text-sm text-gray-500">
                                                                        Instructor: {inscripcion.instructor_nombre}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                                  inscripcion.estado_pago === 'habilitado' ? 'bg-green-100 text-green-800' :
                                                                      inscripcion.estado_pago === 'pendiente' ? 'bg-yellow-100 text-yellow-800' :
                                                                          'bg-red-100 text-red-800'
                                                              }`}>
                                                                  {inscripcion.estado_pago === 'habilitado' ? 'Activa' :
                                                                      inscripcion.estado_pago === 'pendiente' ? 'Pendiente' : 'Inactiva'}
                                                              </span>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    {inscripcion.es_gratuito ? 'Gratuito' : `$${inscripcion.precio}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Estadísticas</h4>
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-medico-blue">
                                                    {selectedUser.total_inscripciones || 0}
                                                </div>
                                                <div className="text-sm text-gray-500">Total Inscripciones</div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">
                                                    {selectedUser.inscripciones_activas || 0}
                                                </div>
                                                <div className="text-sm text-gray-500">Inscripciones Activas</div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">
                                                    {selectedUser.clases_completadas || 0}
                                                </div>
                                                <div className="text-sm text-gray-500">Clases Completadas</div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-orange-600">
                                                    {selectedUser.simulacros_realizados || 0}
                                                </div>
                                                <div className="text-sm text-gray-500">Simulacros Realizados</div>
                                            </div>
                                        </div>

                                        {selectedUser.promedio_simulacros && selectedUser.promedio_simulacros > 0 && (
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-600">
                                                        {Math.round(selectedUser.promedio_simulacros)}%
                                                    </div>
                                                    <div className="text-sm text-gray-500">Promedio Simulacros</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 space-y-3">
                                        <button
                                            onClick={() => {
                                                setShowViewModal(false)
                                                handleUserEdit(selectedUser)
                                            }}
                                            className="w-full bg-medico-blue text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
                                        >
                                            Editar Usuario
                                        </button>
                                        {/*<button*/}
                                        {/*    onClick={() => handleUserViewProgress(selectedUser)}*/}
                                        {/*    className="w-full bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700 transition-colors"*/}
                                        {/*>*/}
                                        {/*    Ver Progreso Detallado*/}
                                        {/*</button>*/}
                                        <button
                                            onClick={() => {
                                                setShowViewModal(false)
                                                handleUserPasswordReset(selectedUser)
                                            }}
                                            className="w-full bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-colors"
                                        >
                                            Resetear Contraseña
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de confirmación de eliminación */}
                {showDeleteConfirm && selectedUser && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Confirmar Eliminación</h3>
                                <p className="text-gray-600 mt-2">
                                    ¿Estás seguro de que quieres eliminar al usuario <strong>"{selectedUser.nombre_completo}"</strong>?
                                    Esta acción desactivará la cuenta pero conservará todos sus datos.
                                </p>
                                {selectedUser.total_inscripciones > 0 && (
                                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-sm text-yellow-800">
                                            <strong>Atención:</strong> Este usuario tiene {selectedUser.total_inscripciones} inscripción(es).
                                            Al eliminarlo se desactivará pero mantendrá su historial académico.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteConfirm(false)
                                        setSelectedUser(null)
                                    }}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDeleteUser}
                                    disabled={formLoading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                                >
                                    {formLoading && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                    <span>{formLoading ? 'Eliminando...' : 'Eliminar Usuario'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de reset de contraseña */}
                {showPasswordReset && selectedUser && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Resetear Contraseña</h3>
                                <p className="text-gray-600 mt-2">
                                    Establece una nueva contraseña para <strong>{selectedUser.nombre_completo}</strong>
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nueva Contraseña *
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-medico-blue focus:border-transparent"
                                    placeholder="Mínimo 6 caracteres"
                                    minLength={6}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    La nueva contraseña será enviada al usuario por email
                                </p>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowPasswordReset(false)
                                        setSelectedUser(null)
                                        setNewPassword('')
                                    }}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmPasswordReset}
                                    disabled={formLoading || !newPassword || newPassword.length < 6}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                                >
                                    {formLoading && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                    <span>{formLoading ? 'Reseteando...' : 'Resetear Contraseña'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para ver estudiantes de un curso */}
                {showCourseStudentsModal && selectedCourse && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl w-full max-w-6xl flex flex-col" style={{maxHeight: '90vh'}}>
                            {/* Header fijo */}
                            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                                <div>
                                    <h2 className="text-2xl font-bold text-medico-blue">Estudiantes del Curso</h2>
                                    <p className="text-gray-600 mt-1">{selectedCourse.titulo}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowCourseStudentsModal(false)
                                        setSelectedCourse(null)
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Estadísticas del curso - fijas */}
                            <div className="px-6 pt-4 pb-2 border-b border-gray-200">
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                        <div className="text-xs text-blue-600 font-medium">Total Habilitados</div>
                                        <div className="text-2xl font-bold text-blue-700">{selectedCourse.habilitados}</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                        <div className="text-xs text-green-600 font-medium">Accesos Activos</div>
                                        <div className="text-2xl font-bold text-green-700">
                                            {selectedCourse.inscripciones?.filter(e => e.estado_pago === 'habilitado' && e.acceso_activo === true).length || 0}
                                        </div>
                                    </div>
                                    <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                                        <div className="text-xs text-orange-600 font-medium">Accesos Inactivos</div>
                                        <div className="text-2xl font-bold text-orange-700">
                                            {selectedCourse.inscripciones?.filter(e => e.estado_pago === 'habilitado' && e.acceso_activo === false).length || 0}
                                        </div>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                        <div className="text-xs text-yellow-600 font-medium">Pendientes de Pago</div>
                                        <div className="text-2xl font-bold text-yellow-700">{selectedCourse.pendientes}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Barra de búsqueda y filtros */}
                            <div className="px-6 pt-4 pb-2 border-b border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    {/* Campo de búsqueda */}
                                    <div className="flex-1 max-w-md">
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>
                                            <input
                                                type="text"
                                                value={studentSearch}
                                                onChange={(e) => setStudentSearch(e.target.value)}
                                                placeholder="Buscar por nombre, usuario o email..."
                                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-medico-blue focus:border-medico-blue sm:text-sm"
                                            />
                                            {studentSearch && (
                                                <button
                                                    onClick={() => setStudentSearch('')}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                >
                                                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Filtros y acciones */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <label className="text-sm font-medium text-gray-700">Estado:</label>
                                        <select
                                            value={studentFilter}
                                            onChange={(e) => setStudentFilter(e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-medico-blue focus:border-medico-blue"
                                        >
                                            <option value="todos">Todos los habilitados</option>
                                            <option value="activos">Solo activos</option>
                                            <option value="inactivos">Solo inactivos</option>
                                        </select>
                                        {selectedStudents.length > 0 && (
                                            <span className="text-sm text-medico-blue font-medium">
                                                {selectedStudents.length} seleccionado(s)
                                            </span>
                                        )}
                                        {studentSearch && (
                                            <span className="text-xs text-gray-500">
                                                ({getFilteredStudents().length} resultado(s))
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        {selectedStudents.length > 0 && (
                                            <>
                                                <button
                                                    onClick={handleSuspendAccess}
                                                    disabled={actionLoading}
                                                    className="inline-flex items-center px-3 py-1.5 border border-orange-500 text-sm font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 transition-colors disabled:opacity-50"
                                                >
                                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                    </svg>
                                                    Desactivar
                                                </button>
                                                <button
                                                    onClick={handleReactivateAccess}
                                                    disabled={actionLoading}
                                                    className="inline-flex items-center px-3 py-1.5 border border-green-500 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 transition-colors disabled:opacity-50"
                                                >
                                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Reactivar
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Lista de estudiantes con scroll */}
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                            {getFilteredStudents().length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedStudents.length === getFilteredStudents().length && getFilteredStudents().length > 0}
                                                        onChange={handleSelectAll}
                                                        className="h-4 w-4 text-medico-blue focus:ring-medico-blue border-gray-300 rounded"
                                                    />
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Estudiante
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Teléfono
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Fecha Inscripción
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Estado Acceso
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {getFilteredStudents().map((enrollment) => (
                                                <tr key={enrollment.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStudents.includes(enrollment.id)}
                                                            onChange={() => handleSelectStudent(enrollment.id)}
                                                            className="h-4 w-4 text-medico-blue focus:ring-medico-blue border-gray-300 rounded"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center">
                                                            <div className="flex-shrink-0 h-8 w-8">
                                                                <div className="h-8 w-8 rounded-full bg-medico-blue flex items-center justify-center">
                                                                    <span className="text-white font-medium text-xs">
                                                                        {enrollment.nombre_completo?.charAt(0)?.toUpperCase() || 'U'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="ml-3">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {enrollment.nombre_completo}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    @{enrollment.nombre_usuario}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-sm text-gray-900">{enrollment.email}</div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-sm text-gray-900">
                                                            {enrollment.telefono || '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="text-xs text-gray-500">
                                                            {formatDate(enrollment.fecha_inscripcion)}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                            enrollment.acceso_activo === true
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-orange-100 text-orange-800'
                                                        }`}>
                                                            {enrollment.acceso_activo === true ? '✅ Activo' : '🔒 Inactivo'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                    </svg>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {studentFilter === 'activos' ? 'No hay estudiantes activos' :
                                         studentFilter === 'inactivos' ? 'No hay estudiantes inactivos' :
                                         'No hay estudiantes habilitados'}
                                    </h3>
                                    <p className="text-gray-500">
                                        {studentFilter === 'todos'
                                            ? 'Este curso aún no tiene estudiantes con pagos habilitados'
                                            : 'Prueba cambiando el filtro para ver otros estudiantes'}
                                    </p>
                                </div>
                            )}
                            </div>

                            {/* Footer fijo */}
                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                                <button
                                    onClick={() => {
                                        setShowCourseStudentsModal(false)
                                        setSelectedCourse(null)
                                    }}
                                    className="px-6 py-2 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de confirmación para desactivar/reactivar */}
                {showConfirmModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
                            <div className="flex items-center mb-4">
                                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                                    confirmAction === 'suspend' ? 'bg-orange-100' : 'bg-green-100'
                                }`}>
                                    {confirmAction === 'suspend' ? (
                                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {confirmAction === 'suspend' ? 'Desactivar Accesos' : 'Reactivar Accesos'}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {selectedStudents.length} estudiante(s) seleccionado(s)
                                    </p>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-gray-700">
                                    {confirmAction === 'suspend' ? (
                                        <>
                                            ¿Estás seguro de que deseas <strong className="text-orange-600">desactivar</strong> el acceso a{' '}
                                            <strong>{selectedStudents.length}</strong> estudiante(s)?
                                            <br /><br />
                                            Los estudiantes <strong>NO podrán acceder al curso</strong> hasta que sean reactivados.
                                        </>
                                    ) : (
                                        <>
                                            ¿Estás seguro de que deseas <strong className="text-green-600">reactivar</strong> el acceso a{' '}
                                            <strong>{selectedStudents.length}</strong> estudiante(s)?
                                            <br /><br />
                                            Los estudiantes <strong>recuperarán el acceso completo al curso</strong>.
                                        </>
                                    )}
                                </p>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false)
                                        setConfirmAction(null)
                                    }}
                                    disabled={actionLoading}
                                    className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={executeConfirmedAction}
                                    disabled={actionLoading}
                                    className={`px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 flex items-center ${
                                        confirmAction === 'suspend'
                                            ? 'bg-orange-600 hover:bg-orange-700'
                                            : 'bg-green-600 hover:bg-green-700'
                                    }`}
                                >
                                    {actionLoading && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                    )}
                                    {actionLoading ? 'Procesando...' : confirmAction === 'suspend' ? 'Desactivar' : 'Reactivar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    )
}

export default AdminUsers